import { isDataWidgetBlock } from '@/lib/blocks/collection/data-widget/blocks';
import { resolveBlockProps } from '@/lib/blocks/prop-resolver';
import { getMainSections } from '@/lib/page-utils';
import { resolveRemappedId } from '@/stores/page-editor-middleware/autosave-id-remap';
import { usePageEditorStore } from '@/stores/page-editor-store';
import type { PageBlock } from '@/types/block';

interface HydrationOptions {
  previewOnly?: boolean;
}

export async function hydrateDataWidgetBlocksForSection(
  sectionId: string,
  options: HydrationOptions = {},
): Promise<void> {
  const state = usePageEditorStore.getState();
  const page = state.page;
  if (!page) return;

  const resolvedSectionId = resolveRemappedId(sectionId, state.incomingSectionIdMappings ?? {});
  const section = getMainSections(page).find((sec) => sec.id === resolvedSectionId);
  if (!section?.blocks) return;

  const widgetBlocks = section.blocks.filter(isDataWidgetBlock);
  if (widgetBlocks.length === 0) return;

  try {
    const resolvedPropsById = await resolveBlockProps(
      section.blocks,
      page.id,
      undefined,
      section.sectionTemplateCode,
    );
    if (usePageEditorStore.getState().page?.id !== page.id) return;

    const updates: Array<{ blockId: string; updates: Partial<PageBlock> }> = [];
    for (const widget of widgetBlocks) {
      const resolved = resolvedPropsById.get(widget.id);
      if (!resolved) continue;
      updates.push({
        blockId: widget.id,
        updates: { props: { ...(widget.props ?? {}), ...resolved } },
      });
    }
    if (updates.length === 0) return;

    const store = usePageEditorStore.getState();
    if (options.previewOnly) {
      for (const { blockId, updates: blockUpdates } of updates) {
        store.updateBlockPreview(resolvedSectionId, blockId, blockUpdates);
      }
    } else {
      store.batchUpdateBlocks(resolvedSectionId, updates);
    }
  } catch {
    // Leave blocks unhydrated; renderer falls back to whatever props are present.
  }
}

export async function hydrateDataWidgetBlocksForSections(
  sectionIds: Iterable<string>,
  options: HydrationOptions = {},
): Promise<void> {
  const unique = new Set<string>();
  for (const id of sectionIds) {
    if (id) unique.add(id);
  }
  if (unique.size === 0) return;
  await Promise.all([...unique].map((id) => hydrateDataWidgetBlocksForSection(id, options)));
}
